import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

import { cn } from "@/lib/utils";
import { useScrollLock, useTouchScrollLock } from "@/hooks/useScrollLock";

const HoverCard = HoverCardPrimitive.Root;

const HoverCardTrigger = HoverCardPrimitive.Trigger;

const HoverCardArrow = HoverCardPrimitive.Arrow;

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  // 监听 HoverCard 的打开/关闭状态
  React.useEffect(() => {
    const content = ref && 'current' in ref ? ref.current : null;
    if (!content) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-state') {
          const state = content.getAttribute('data-state');
          setIsOpen(state === 'open');
        }
      });
    });

    observer.observe(content, {
      attributes: true,
      attributeFilter: ['data-state']
    });

    // 初始状态检查
    const initialState = content.getAttribute('data-state');
    setIsOpen(initialState === 'open');

    return () => observer.disconnect();
  }, [ref]);

  // 移动端滚动锁定
  useScrollLock(isOpen);
  useTouchScrollLock(isOpen);

  return (
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-hover-card-content-transform-origin]",
        // 移动端优化样式
        "touch-none select-none",
        className
      )}
      {...props}
    />
  );
});
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent, HoverCardArrow };
